/**
 * Wallet · Financial Profile — 6-step wizard aligned to the design.
 *
 * Steps: Income · Commitments · Buffer · Experience · Goals · Review
 * Then a "Profile complete" celebration screen.
 *
 * Design principles
 *   • One question per screen · plain language · ~15-20s each
 *   • Autosave after every answer via PUT /api/wallet/financial-profile
 *   • "Save & finish later" on every step (routes back to /wallet)
 *   • Progress bar with numbered stepper
 *   • No scoring, no labels — factual, non-judgmental
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Wallet, Home, PiggyBank, LineChart, Target, FileText,
  ChevronLeft, ArrowRight, Check, Loader2, Timer, ShieldCheck, Ban, RefreshCw,
} from "lucide-react";
import { WalletShell, NAVY, GOLD, BG } from "@/components/wallet/WalletShell";
import { loadWallet } from "@/lib/wallet-storage";
import NaviFace from "@/components/wallet/NaviFace";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const STEPS = [
  { key: "income",       label: "Income",       title: "Your monthly take-home income",   hint: "Approximately how much money do you receive each month after tax?",     seconds: 15, icon: Wallet,
    naviQ: "What counts as monthly take-home income? Should I include bonuses or side-hustle money?" },
  { key: "commitments",  label: "Commitments",  title: "Essential living costs",           hint: "Approximately how much do you spend on essential living costs each month?", helper: "Think about housing, food, utilities, transport, education and other essential household expenses.", seconds: 15, icon: Home,
    naviQ: "What counts as an essential living cost — and what doesn't?" },
  { key: "buffer",       label: "Buffer",       title: "If your income stopped unexpectedly", hint: "Approximately how long could you cover your normal living expenses?", seconds: 15, icon: PiggyBank,
    naviQ: "Why does knowing my financial buffer matter, and how do I work it out?" },
  { key: "experience",   label: "Experience",   title: "Your financial experience",        hint: "How would you describe your experience with the following?",           seconds: 20, icon: LineChart,
    naviQ: "What's the difference between 'Some' and 'Experienced' when I answer these?" },
  { key: "goals",        label: "Goals",        title: "Your current goals",               hint: "Select any that apply.",                                              seconds: 15, icon: Target,
    naviQ: "I'm not sure which goals to pick — can you help me think about it?" },
  { key: "review",       label: "Review",       title: "Review your profile",              hint: "Please check that your answers look correct. You can edit them at any time.", seconds: 10, icon: FileText,
    naviQ: "How will Altara use these answers, and who else can see them?" },
];

const BUFFER_LABELS = {
  less_than_1_month:  "Less than one month",
  "1_to_3_months":    "1 – 3 months",
  "3_to_6_months":    "3 – 6 months",
  more_than_6_months: "More than 6 months",
  not_sure:           "I'm not sure",
  prefer_not:         "Prefer not to say",
};
const EXP_CATS = [
  ["banking",      "Banking"],
  ["insurance",    "Insurance"],
  ["investing",    "Investing"],
  ["retirement",   "Retirement products"],
  ["credit",       "Credit"],
  ["home_finance", "Home finance"],
];
const EXP_LEVELS = [["new", "New"], ["some", "Some"], ["experienced", "Experienced"]];
const GOALS = [
  ["build_emergency_savings",  "Build emergency savings"],
  ["reduce_debt",              "Reduce debt"],
  ["buy_a_home",               "Buy a home"],
  ["protect_my_family",        "Protect my family"],
  ["save_for_education",       "Save for education"],
  ["start_investing",          "Start investing"],
  ["grow_my_investments",      "Grow my investments"],
  ["plan_for_retirement",      "Plan for retirement"],
  ["start_or_grow_a_business", "Start or grow a business"],
  ["something_else",           "Something else"],
  ["not_sure_yet",             "I'm not sure yet"],
];

export default function WalletFinancialProfile() {
  const nav = useNavigate();
  const wallet = loadWallet() || {};
  const deviceId = wallet.device_id;

  const [step, setStep] = useState(0);
  const [options, setOptions] = useState(null);
  const [profile, setProfile] = useState({
    income_band: null,
    essential_costs_band: null,
    buffer_months: null,
    experience: {},
    goals: [],
    completed_at: null,
  });
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [completed, setCompleted] = useState(false);

  // Load reference options + existing profile once
  useEffect(() => {
    if (!deviceId) return;
    Promise.all([
      axios.get(`${API}/wallet/financial-profile/reference/options`),
      axios.get(`${API}/wallet/financial-profile`, { params: { device_id: deviceId } }),
    ]).then(([opt, cur]) => {
      setOptions(opt.data);
      if (cur.data?.exists) {
        const p = cur.data.profile;
        setProfile({
          income_band:          p.income_band || null,
          essential_costs_band: p.essential_costs_band || null,
          buffer_months:        p.buffer_months || null,
          experience:           p.experience || {},
          goals:                p.goals || [],
          completed_at:         p.completed_at || null,
        });
        // Resume at the first unanswered step
        const answered = [p.income_band, p.essential_costs_band, p.buffer_months, Object.keys(p.experience || {}).length > 0, (p.goals || []).length > 0].map(Boolean);
        const idx = answered.findIndex(v => !v);
        setStep(idx === -1 ? 5 : idx);
        if (p.completed_at) setCompleted(true);
      }
    }).catch(() => { /* silent; the form still works */ });
  }, [deviceId]);

  const autosave = async (patch) => {
    if (!deviceId) return;
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/wallet/financial-profile`, { device_id: deviceId, ...patch });
      setSavedAt(new Date());
      // Merge returned canonical state
      if (data) {
        setProfile((prev) => ({
          ...prev,
          income_band:          data.income_band ?? prev.income_band,
          essential_costs_band: data.essential_costs_band ?? prev.essential_costs_band,
          buffer_months:        data.buffer_months ?? prev.buffer_months,
          experience:           data.experience ?? prev.experience,
          goals:                data.goals ?? prev.goals,
          completed_at:         data.completed_at ?? prev.completed_at,
        }));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const canContinue = useMemo(() => {
    switch (STEPS[step]?.key) {
      case "income":      return Boolean(profile.income_band);
      case "commitments": return Boolean(profile.essential_costs_band);
      case "buffer":      return Boolean(profile.buffer_months);
      case "experience":  return Object.keys(profile.experience || {}).length >= 3;
      case "goals":       return (profile.goals || []).length >= 1;
      case "review":      return true;
      default:            return false;
    }
  }, [step, profile]);

  const finish = async () => {
    await autosave({ completed: true });
    setCompleted(true);
  };

  if (!deviceId) {
    return (
      <WalletShell activeTab="profile">
        <div className="max-w-lg mx-auto p-6 text-center text-slate-500">
          Wallet not initialised — please open the wallet from Home first.
        </div>
      </WalletShell>
    );
  }

  if (completed) return <CompletionScreen onGoToPassport={() => nav("/wallet")} onEdit={() => setCompleted(false)} />;

  const cur = STEPS[step];
  const totalSeconds = STEPS.reduce((n, s) => n + s.seconds, 0);
  const stepSeconds = cur.seconds;

  return (
    <WalletShell activeTab="profile">
      <div className="max-w-2xl mx-auto px-4 lg:px-8 py-6 lg:py-8 space-y-6" data-testid="wallet-financial-profile">
        {/* Title header */}
        <header className="text-center">
          <h1 className="font-display text-3xl lg:text-4xl font-semibold" style={{ color: NAVY }}>My Financial Profile</h1>
          <p className="text-[13.5px] text-slate-600 mt-1.5">A few simple questions. A clearer picture. A more relevant experience.</p>
          <div className="mt-3 inline-flex items-center gap-2 rounded-full px-3 py-1.5 border" style={{ background: `${GOLD}18`, borderColor: `${GOLD}55` }}>
            <Timer className="w-3.5 h-3.5" style={{ color: NAVY }} />
            <span className="text-[11.5px] font-medium" style={{ color: NAVY }}>
              Approximately {Math.round(totalSeconds / 10) * 10} seconds — you can save and finish later at any time
            </span>
          </div>
        </header>

        {/* Numbered stepper */}
        <Stepper step={step} onStep={setStep} completed={completed} />

        {/* Card */}
        <section className="rounded-2xl bg-white border border-slate-100 shadow-sm p-5 lg:p-6" data-testid={`fp-step-${cur.key}`}>
          <div className="flex items-center justify-between mb-1">
            <div className="text-[11px] uppercase tracking-widest text-slate-500">Step {step + 1}</div>
            <div className="text-[11px] text-slate-500">~{stepSeconds} seconds</div>
          </div>

          {/* Progress micro-bar */}
          <div className="h-1 bg-slate-100 rounded-full overflow-hidden mb-5">
            <div className="h-full rounded-full transition-all" style={{ width: `${((step + 1) / STEPS.length) * 100}%`, background: NAVY }} />
          </div>

          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: `${GOLD}22` }}>
              <cur.icon className="w-5 h-5" style={{ color: NAVY }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display text-[17px] lg:text-[18px] font-semibold" style={{ color: NAVY }}>{cur.title}</div>
              <p className="text-[12.5px] text-slate-600 mt-1 leading-relaxed">{cur.hint}</p>
              {cur.helper && <p className="text-[11.5px] text-slate-500 mt-2 leading-relaxed italic">{cur.helper}</p>}
              {cur.naviQ && (
                <button
                  onClick={() => window.dispatchEvent(new CustomEvent("navi.open", { detail: { question: cur.naviQ } }))}
                  data-testid={`fp-navi-hint-${cur.key}`}
                  className="mt-3 inline-flex items-center gap-2 rounded-full pl-1 pr-3 py-1 border transition hover:shadow-sm"
                  style={{ borderColor: `${GOLD}55`, background: `${GOLD}12` }}
                >
                  <NaviFace size="xs" ring={false} />
                  <span className="text-[11.5px] font-medium" style={{ color: NAVY }}>
                    Ask NAVI: &ldquo;{cur.naviQ}&rdquo;
                  </span>
                </button>
              )}
            </div>
          </div>

          <div className="mt-5">
            {cur.key === "income" && options && (
              <RadioGrid
                value={profile.income_band}
                options={options.income_bands.map(b => ({ code: b.code, label: b.label }))}
                onPick={(code) => { setProfile(p => ({ ...p, income_band: code })); autosave({ income_band: code }); }}
                testidPrefix="fp-income"
              />
            )}
            {cur.key === "commitments" && options && (
              <RadioGrid
                value={profile.essential_costs_band}
                options={options.income_bands.map(b => ({ code: b.code, label: b.label }))}
                onPick={(code) => { setProfile(p => ({ ...p, essential_costs_band: code })); autosave({ essential_costs_band: code }); }}
                testidPrefix="fp-costs"
              />
            )}
            {cur.key === "buffer" && (
              <RadioGrid
                value={profile.buffer_months}
                options={Object.entries(BUFFER_LABELS).map(([code, label]) => ({ code, label }))}
                onPick={(code) => { setProfile(p => ({ ...p, buffer_months: code })); autosave({ buffer_months: code }); }}
                testidPrefix="fp-buffer"
              />
            )}
            {cur.key === "experience" && (
              <ExperienceGrid
                value={profile.experience || {}}
                onSet={(cat, lvl) => {
                  const next = { ...(profile.experience || {}), [cat]: lvl };
                  setProfile(p => ({ ...p, experience: next }));
                  autosave({ experience: next });
                }}
              />
            )}
            {cur.key === "goals" && (
              <GoalsGrid
                value={profile.goals || []}
                onToggle={(g) => {
                  const list = new Set(profile.goals || []);
                  if (list.has(g)) list.delete(g); else list.add(g);
                  const next = [...list];
                  setProfile(p => ({ ...p, goals: next }));
                  autosave({ goals: next });
                }}
              />
            )}
            {cur.key === "review" && options && (
              <ReviewSummary profile={profile} options={options} onEdit={setStep} />
            )}
          </div>
        </section>

        {/* Action row */}
        <div className="flex items-center justify-between gap-3">
          <button
            onClick={() => setStep(s => Math.max(0, s - 1))}
            disabled={step === 0}
            data-testid="fp-back"
            className="inline-flex items-center gap-1 text-[13px] text-slate-600 disabled:opacity-40 hover:text-slate-900"
          >
            <ChevronLeft className="w-4 h-4" /> Back
          </button>
          <div className="text-[11px] text-slate-400 flex items-center gap-1" data-testid="fp-saved-indicator">
            {saving ? (<><Loader2 className="w-3 h-3 animate-spin" /> Saving…</>) : savedAt ? (<><Check className="w-3 h-3 text-emerald-600" /> Saved</>) : ""}
          </div>
          {step < STEPS.length - 1 ? (
            <button
              onClick={() => setStep(s => Math.min(STEPS.length - 1, s + 1))}
              disabled={!canContinue}
              data-testid="fp-next"
              className="inline-flex items-center gap-1 rounded-full px-5 py-2.5 text-[13px] font-semibold text-white disabled:opacity-40"
              style={{ background: NAVY }}
            >
              Continue <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={finish}
              disabled={!canContinue || saving}
              data-testid="fp-finish"
              className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[13px] font-semibold text-white disabled:opacity-40"
              style={{ background: NAVY }}
            >
              {saving ? (<><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>) : "Complete my profile"}
            </button>
          )}
        </div>

        <div className="text-center">
          <button
            onClick={() => nav("/wallet")}
            data-testid="fp-save-exit"
            className="text-[12px] text-slate-500 hover:underline"
          >
            Save &amp; finish later
          </button>
        </div>
      </div>
    </WalletShell>
  );
}

/* ─────────────────────────── Stepper ──────────────────────────── */
function Stepper({ step, onStep }) {
  return (
    <div className="flex items-center justify-between gap-1" data-testid="fp-stepper">
      {STEPS.map((s, i) => {
        const active = i === step;
        const done = i < step;
        return (
          <button
            key={s.key}
            onClick={() => (done ? onStep(i) : undefined)}
            data-testid={`fp-step-btn-${s.key}`}
            disabled={!done}
            className="flex-1 flex flex-col items-center gap-1 group"
          >
            <div className="flex items-center gap-1 w-full">
              <span
                className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold flex-shrink-0"
                style={{
                  background: active ? NAVY : done ? GOLD : "#F1F5F9",
                  color: active ? "white" : done ? "white" : "#94A3B8",
                }}
              >
                {done ? <Check className="w-3.5 h-3.5" /> : i + 1}
              </span>
              {i < STEPS.length - 1 && <span className="flex-1 h-px bg-slate-200" />}
            </div>
            <span className="text-[10px] text-slate-500 truncate max-w-full" style={{ color: active ? NAVY : "#94A3B8" }}>{s.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/* ────────────────────────── Radio grid ────────────────────────── */
function RadioGrid({ value, options, onPick, testidPrefix }) {
  return (
    <div className="grid grid-cols-1 gap-2">
      {options.map(o => {
        const selected = value === o.code;
        return (
          <button
            key={o.code}
            onClick={() => onPick(o.code)}
            data-testid={`${testidPrefix}-${o.code}`}
            className="flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition"
            style={{
              background: selected ? `${GOLD}18` : "white",
              borderColor: selected ? NAVY : "#E2E8F0",
            }}
          >
            <span className="w-4 h-4 rounded-full border flex items-center justify-center flex-shrink-0" style={{ borderColor: selected ? NAVY : "#CBD5E1" }}>
              {selected && <span className="w-2 h-2 rounded-full" style={{ background: NAVY }} />}
            </span>
            <span className="text-[13.5px]" style={{ color: NAVY }}>{o.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/* ──────────────────────── Experience grid ─────────────────────── */
function ExperienceGrid({ value, onSet }) {
  return (
    <div className="space-y-2.5">
      {EXP_CATS.map(([code, label]) => (
        <div key={code} className="flex items-center gap-3">
          <div className="flex-1 text-[13px] font-medium" style={{ color: NAVY }}>{label}</div>
          <div className="inline-flex rounded-full p-0.5 border border-slate-200 bg-white">
            {EXP_LEVELS.map(([lvlCode, lvlLabel]) => {
              const active = value[code] === lvlCode;
              return (
                <button
                  key={lvlCode}
                  onClick={() => onSet(code, lvlCode)}
                  data-testid={`fp-exp-${code}-${lvlCode}`}
                  className="px-3 py-1.5 rounded-full text-[11px] font-semibold transition"
                  style={{
                    background: active ? NAVY : "transparent",
                    color: active ? "white" : "#334155",
                  }}
                >
                  {lvlLabel}
                </button>
              );
            })}
          </div>
        </div>
      ))}
      <div className="text-[11px] text-slate-500 mt-2">Answer at least three to continue.</div>
    </div>
  );
}

/* ─────────────────────────── Goals grid ────────────────────────── */
function GoalsGrid({ value, onToggle }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {GOALS.map(([code, label]) => {
        const selected = value.includes(code);
        return (
          <button
            key={code}
            onClick={() => onToggle(code)}
            data-testid={`fp-goal-${code}`}
            className="flex items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition"
            style={{
              background: selected ? `${GOLD}18` : "white",
              borderColor: selected ? NAVY : "#E2E8F0",
            }}
          >
            <span
              className="w-4 h-4 rounded-md border flex items-center justify-center flex-shrink-0"
              style={{
                borderColor: selected ? NAVY : "#CBD5E1",
                background: selected ? NAVY : "white",
              }}
            >
              {selected && <Check className="w-3 h-3 text-white" />}
            </span>
            <span className="text-[13px]" style={{ color: NAVY }}>{label}</span>
          </button>
        );
      })}
    </div>
  );
}

/* ─────────────────────── Review summary ────────────────────────── */
function ReviewSummary({ profile, options, onEdit }) {
  const bandLabel = (code) => options.income_bands.find(b => b.code === code)?.label || "—";
  const rows = [
    { label: "Monthly income",  value: bandLabel(profile.income_band),          onEdit: () => onEdit(0) },
    { label: "Essential costs", value: bandLabel(profile.essential_costs_band), onEdit: () => onEdit(1) },
    { label: "Financial buffer", value: BUFFER_LABELS[profile.buffer_months] || "—", onEdit: () => onEdit(2) },
    { label: "Experience",      value: Object.keys(profile.experience || {}).length > 0 ? `${Object.keys(profile.experience).length} answered` : "—", onEdit: () => onEdit(3) },
    { label: "Goals",           value: (profile.goals || []).length > 0 ? `${profile.goals.length} selected` : "—", onEdit: () => onEdit(4) },
  ];
  return (
    <div className="rounded-xl border border-slate-100 divide-y divide-slate-100">
      {rows.map((r) => (
        <div key={r.label} className="flex items-center justify-between px-4 py-3">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-400">{r.label}</div>
            <div className="text-[13.5px] font-medium" style={{ color: NAVY }}>{r.value}</div>
          </div>
          <button onClick={r.onEdit} data-testid={`fp-review-edit-${r.label.toLowerCase().replace(/[^a-z]/g,'-')}`} className="text-[12px] font-semibold hover:underline" style={{ color: NAVY }}>Edit</button>
        </div>
      ))}
    </div>
  );
}

/* ────────────────────── Completion screen ─────────────────────── */
function CompletionScreen({ onGoToPassport, onEdit }) {
  return (
    <WalletShell activeTab="profile">
      <div className="max-w-lg mx-auto px-4 py-10 text-center" data-testid="fp-completed">
        <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-4" style={{ background: "#D1FAE5" }}>
          <Check className="w-6 h-6 text-emerald-700" />
        </div>
        <h1 className="font-display text-2xl lg:text-3xl font-semibold" style={{ color: NAVY }}>Your Financial Profile is ready</h1>
        <p className="text-[13.5px] text-slate-600 mt-2 max-w-md mx-auto leading-relaxed">
          Altara now has some basic context to help make financial information and future decisions more relevant to you.
        </p>
        <div className="mt-6 grid grid-cols-3 gap-2">
          <Pillar icon={ShieldCheck} label="This isn't a credit score" />
          <Pillar icon={Ban}         label="Not used to sell or recommend products" />
          <Pillar icon={RefreshCw}   label="You can update it any time" />
        </div>
        <button onClick={onGoToPassport} data-testid="fp-completed-go" className="mt-7 inline-flex items-center gap-2 rounded-full px-6 py-3 text-[14px] font-semibold text-white" style={{ background: NAVY }}>
          Go to my Passport <ArrowRight className="w-4 h-4" />
        </button>
        <div className="mt-3">
          <button onClick={onEdit} data-testid="fp-completed-edit" className="text-[12px] text-slate-500 hover:underline">Edit my profile</button>
        </div>
      </div>
    </WalletShell>
  );
}
function Pillar({ icon: Icon, label }) {
  return (
    <div className="rounded-xl bg-white border border-slate-100 p-3 flex flex-col items-center gap-1">
      <Icon className="w-4 h-4" style={{ color: NAVY }} />
      <div className="text-[10.5px] text-slate-600 leading-tight">{label}</div>
    </div>
  );
}
